import backoff from '@articulate/funky/lib/backoff'
import { LogoutFunc, SignOutReason } from 'lib/hooks/useAuth'
import { isLoggedIn, Self } from 'lib/util/self'

import { authiculateClient, refreshSession as refreshOktaSession } from './authiculate'

const endDateKey = 'endDate'

const wait = (time: number) =>
  new Promise(resolve => setTimeout(resolve, time))

const isSessionExpiredError = (error: Error | Error & { xhr?: { status: number }, errorCode?: string }) => {
  if ('errorCode' in error) {
    return error.errorCode === 'E0000007'
  }

  if ('xhr' in error) {
    error.xhr?.status === 404
  }

  return false
}

const refreshSessionWithBackoff = backoff(
  {
    base: 1000,
    tries: 5,
    when: (error) => !isSessionExpiredError(error)
  },
  refreshOktaSession)

let refreshSessionPromise: Promise<void | object>

export const refreshSession = (self: Self, logout: LogoutFunc) => {
  if (!refreshSessionPromise && isLoggedIn(self)) {
    refreshSessionPromise = refreshSessionWithBackoff()
      .catch((error) => {
        if (isSessionExpiredError(error)) {
          logout(SignOutReason.InvalidSession, 'Okta session expired')
        }
      })
      .finally(async() => {
        await wait(30 * 1000)
        refreshSessionPromise = null
      })
  }
}

const getSavedEndDate = () => {
  const storedDate = window.localStorage.getItem(endDateKey)

  return storedDate ? new Date(storedDate) : null
}

export const clearSessionEndDate = () => window.localStorage.removeItem(endDateKey)

const normalizeDate = (date: Date) => new Date(new Date(date).setHours(0, 0, 0, 0)).toISOString()

const saveEndDate = (date: Date) => window.localStorage.setItem(endDateKey, normalizeDate(date))

export const setSessionEndDate = async() => {
  if (getSavedEndDate()) {
    return
  }

  return authiculateClient.fetchSession().then((session) =>
    saveEndDate(((session as unknown) as { expiresAt: Date }).expiresAt)
  )
}

export const isSignoutExpected = () => {
  const endDate = getSavedEndDate()

  return !endDate || endDate.getTime() - new Date().setHours(0, 0, 0, 0) <= 0
}
